<!--
  Sort column list for PaginationFilter.
  The parent owns saved vs playground drafts, reset, and apply. No API calls.
-->
<template>
  <div class="pagination-filter__sort-list" :data-testid="`${testIdPrefix}__sort-list`">
    <div class="pagination-filter__search">
      <input
        v-model="sortSearch"
        type="text"
        class="pagination-filter__input noTransform"
        :data-testid="`${testIdPrefix}__sort-search`"
        placeholder="Search columns"
        :disabled="disabled"
      />
    </div>

    <div class="pagination-filter__sort-items">
      <p v-if="!filteredColumns.length" class="pagination-filter__empty">No matching columns</p>
      <div v-for="column in filteredColumns" :key="column.key" class="pagination-filter__sort-item" :data-testid="`${testIdPrefix}__sort-item--${columnIndex(column.key)}`">
        <div class="pagination-filter__sort-row">
          <div class="pagination-filter__check">
            <input
              :id="checkboxId(column.key)"
              type="checkbox"
              class="noTransform"
              :data-testid="`${testIdPrefix}__sort-checkbox--${columnIndex(column.key)}`"
              :checked="isSelected(column.key)"
              :disabled="disabled"
              @change="$emit('toggle-column', column.key)"
            />
            <label class="pagination-filter__label noTransform" :for="checkboxId(column.key)">{{ column.label || column.key }}</label>
          </div>
          <div v-if="isSelected(column.key)" class="pagination-filter__sort-controls">
            <span class="pagination-filter__order" :data-testid="`${testIdPrefix}__sort-order--${columnIndex(column.key)}`">{{ orderOf(column.key) }}</span>
            <div class="pagination-filter__logic-toggle">
              <button
                type="button"
                :data-testid="`${testIdPrefix}__sort-asc--${columnIndex(column.key)}`"
                :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': directionOf(column.key) === directionAsc }]"
                :disabled="disabled"
                @click="$emit('set-direction', column.key, directionAsc)"
              >
                Asc
              </button>
              <button
                type="button"
                :data-testid="`${testIdPrefix}__sort-desc--${columnIndex(column.key)}`"
                :class="['pagination-filter__logic-btn', { 'pagination-filter__logic-btn--active': directionOf(column.key) === directionDesc }]"
                :disabled="disabled"
                @click="$emit('set-direction', column.key, directionDesc)"
              >
                Desc
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SORT_DIRECTION } from './PaginationFilterUtils.js';

/**
 * Checkbox list of sortable columns. Selection order is the sort priority.
 */
export default {
  name: 'PaginationFilterSort',

  props: {
    testIdPrefix: {
      type: String,
      default: 'pagination-filter',
    },
    columns: {
      type: Array,
      default: () => [],
    },
    sortItems: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      sortSearch: '',
      directionAsc: SORT_DIRECTION.ASC,
      directionDesc: SORT_DIRECTION.DESC,
    };
  },

  computed: {
    filteredColumns() {
      const term = (this.sortSearch || '').trim().toLowerCase();
      if (!term) return this.columns;
      return (this.columns || []).filter((column) => {
        const label = String(column.label || column.key || '').toLowerCase();
        return label.includes(term) || String(column.key || '').toLowerCase().includes(term);
      });
    },
  },

  beforeDestroy() {
    this.sortSearch = '';
  },

  methods: {
    columnIndex(field) {
      const index = (this.columns || []).findIndex((column) => column.key === field);
      return index >= 0 ? index : 0;
    },

    checkboxId(field) {
      return `${this.testIdPrefix}__sort-checkbox-input--${this.columnIndex(field)}`;
    },

    isSelected(field) {
      return (this.sortItems || []).some((item) => item.field === field);
    },

    orderOf(field) {
      const index = (this.sortItems || []).findIndex((item) => item.field === field);
      return index >= 0 ? index + 1 : '';
    },

    directionOf(field) {
      const item = (this.sortItems || []).find((entry) => entry.field === field);
      return item ? item.direction : this.directionAsc;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './PaginationFilter.scss';
</style>
